import { Injectable, Provider } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  Params,
  Router,
} from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  filter,
  firstValueFrom,
  startWith,
} from 'rxjs';
import { ShopSelectionStore } from '../../state/shop-selection.store';
import { DepartmentsService } from '../departments/departments.service';
import { DetailedCustomer } from '../../../shared/models/customer.model';
import { DepartmentWithResidents } from '../../../shared/models/department.model';

export function provideShopSelection(): Provider {
  return { provide: ShopSelectionStore, useExisting: ShopSelectionService };
}

@Injectable({
  providedIn: 'root',
})
export class ShopSelectionService extends ShopSelectionStore {
  private loadingDepartmentId: number | null = null;
  private readonly unavailableDepartmentIdSubject = new BehaviorSubject<
    number | null
  >(null);

  readonly unavailableDepartmentId$: Observable<number | null> =
    this.unavailableDepartmentIdSubject.asObservable();

  constructor(
    private router: Router,
    private departmentsService: DepartmentsService
  ) {
    super();
  }

  connect(): Subscription {
    const subscription = new Subscription();
    subscription.add(
      this.department$.subscribe(department =>
        this.onDepartmentChange(department)
      )
    );
    subscription.add(
      this.router.events
        .pipe(
          filter(event => event instanceof NavigationEnd),
          startWith(null)
        )
        .subscribe(() => this.syncFromUrl())
    );
    return subscription;
  }

  selectDepartment(departmentId: number): Promise<boolean> {
    return this.mergeQueryParams({ departmentId, residentId: null });
  }

  selectResident(residentId: number): Promise<boolean> {
    return this.mergeQueryParams({ residentId: residentId || null });
  }

  private syncFromUrl(): void {
    this.syncCustomer();
    const customer = this.snapshot.customer;
    if (!customer) return;

    const queryParams = this.queryParams();
    const departmentId = Number(queryParams['departmentId']);
    const selected = this.snapshot.department;

    if (!departmentId) {
      if (selected) this.setDepartment(null);
      this.setResidentId(null);
      this.setUnavailableDepartmentId(null);
      return;
    }

    if (!customer.departments.some(d => d.departmentId === departmentId)) {
      this.setUnavailableDepartmentId(departmentId);
      if (this.isShopRoute()) {
        this.mergeQueryParams({ departmentId: null, residentId: null }, true);
      }
      return;
    }

    if (selected?.departmentId !== departmentId) {
      this.loadDepartment(departmentId);
      return;
    }

    if (this.isShopRoute()) this.syncResident(selected, queryParams);
  }

  private syncCustomer(): void {
    const resolved = this.resolvedCustomer();
    if (resolved === undefined) return;
    if (resolved?.id === this.snapshot.customer?.id) return;
    this.setCustomer(resolved);
    this.setDepartment(null);
    this.setResidentId(null);
  }

  private async loadDepartment(departmentId: number): Promise<void> {
    if (this.loadingDepartmentId === departmentId) return;
    this.loadingDepartmentId = departmentId;
    this.setUnavailableDepartmentId(null);
    let department: DepartmentWithResidents | null = null;
    try {
      const response = await firstValueFrom(
        this.departmentsService.getDepartmentWithResidents(departmentId)
      );
      department = response?.data ?? null;
    } catch {
      department = null;
    } finally {
      if (this.loadingDepartmentId === departmentId) {
        this.loadingDepartmentId = null;
      }
    }

    const stillWanted =
      Number(this.queryParams()['departmentId']) === departmentId;
    if (!stillWanted) return;
    if (department) {
      this.setDepartment(department);
    } else {
      this.setUnavailableDepartmentId(departmentId);
    }
  }

  private setUnavailableDepartmentId(departmentId: number | null): void {
    if (this.unavailableDepartmentIdSubject.value === departmentId) return;
    this.unavailableDepartmentIdSubject.next(departmentId);
  }

  private onDepartmentChange(department: DepartmentWithResidents | null): void {
    if (!department) return;
    const queryParams = this.queryParams();
    const isInUrl =
      Number(queryParams['departmentId']) === department.departmentId;
    if (isInUrl && this.isShopRoute()) {
      this.syncResident(department, queryParams);
    }
  }

  private syncResident(
    department: DepartmentWithResidents,
    queryParams: Params
  ): void {
    const residentId = Number(queryParams['residentId']) || null;
    if (department.people.some(p => p.id === residentId)) {
      this.setResidentId(residentId);
      return;
    }

    const fallbackId = department.people[0]?.id ?? null;
    this.setResidentId(fallbackId);
    if (residentId !== fallbackId) {
      this.mergeQueryParams({ residentId: fallbackId }, true);
    }
  }

  private mergeQueryParams(
    queryParams: Params,
    replaceUrl = false
  ): Promise<boolean> {
    return this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  private queryParams(): Params {
    return this.router.routerState.snapshot.root.queryParams;
  }

  private isShopRoute(): boolean {
    return this.deepestRoute().data['shopHeader'] === true;
  }

  private resolvedCustomer(): DetailedCustomer | null | undefined {
    return this.deepestRoute().data['customer'];
  }

  private deepestRoute(): ActivatedRouteSnapshot {
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) route = route.firstChild;
    return route;
  }
}
